import { Item } from "components/FormControls/Select/Categories";
import { handledCampaignTriggers, handledExportTypes } from "constants/general";
import {
  BackendFilter,
  PossibleConditions,
  SelectedFilter,
} from "types/filters";
import { OLDate } from "utilities/dateFormat";
import { KeyValuePair } from "utilities/objects";
import { array, mixed, object, string } from "yup";
import { exportStatus } from "./FilterBase/Values/ExportStatus";
import { importTypes } from "./FilterBase/Values/ImportType";
import { redemptionCodeStatuses } from "./FilterBase/Values/RedemptionCodeStatus";
import { eventsTypes } from "./FilterBase/Values/Timeline";
import { transactionDocumnetTypes } from "./FilterBase/Values/TransactionDocumentType";

export const isJSON = (val: any) => {
  try {
    JSON.parse(val);
  } catch (_e) {
    return false;
  }
  return true;
};

export const countPercentage = (percent: number, total: number) =>
  parseFloat(((100 * percent) / total).toFixed(1));

export const parseFilterValue = (
  selectedFilter: SelectedFilter,
  message: any,
) => {
  switch (selectedFilter.type) {
    case "boolean":
      return selectedFilter.value;
    case "boolean-status":
      return message({
        id: selectedFilter.value === "true" ? "active" : "inactive",
      });
    case "boolean-yes-no":
      return message({
        id: selectedFilter.value === "true" ? "yes" : "no",
      });
    case "multiwithmodal":
      return JSON.stringify(selectedFilter.value);
    case "direct-campaign-name-filter":
    case "time-campaign-name-filter":
    case "referral-name-filter":
    case "campaign-name-filter":
      return selectedFilter.value.label;
    case "direct-trigger-filter":
    case "referral-trigger-filter":
      const trigger = handledCampaignTriggers.find(
        (trigger) => trigger.id === selectedFilter.value,
      );
      return message({ id: trigger?.label });
    case "country-filter":
      return message({ id: selectedFilter.value.label });
    case "gender":
    case "bulk-actions":
      return message({ id: selectedFilter.value });
    case "timeline-event-type":
      const translatedTimelineEvents = selectedFilter.value.map(
        (timeline: string) =>
          message({
            id: eventsTypes.find((event) => event.value === timeline)?.label,
          }),
      );
      return translatedTimelineEvents.join(", ");
    case "achievement-name":
      return selectedFilter.value?.label || selectedFilter.value;
    case "event-name-filter":
      return selectedFilter.value;
    case "reward-categories":
    case "segments":
    case "tiers":
      return selectedFilter.value
        .map((filter: Item) => filter.label)
        .join(", ");
    case "reward-types-filter":
    case "reward-types-filter_eq":
      return message({ id: selectedFilter.value });
    case "reward-fulfillment-status":
      return typeof selectedFilter.value === "string"
        ? message({ id: selectedFilter.value })
        : selectedFilter.value
            .map((status: string) => message({ id: status }))
            .join(", ");
    case "tenant-name":
    case "channel-filter":
      return selectedFilter.value.label;
    case "import-type":
      return message({
        id: importTypes.find((type) => type.id === selectedFilter.value)?.label,
      });
    case "export-type":
      return message({
        id: handledExportTypes.find((type) => type.id === selectedFilter.value)
          ?.label,
      });
    case "export-status":
      return message({
        id: exportStatus.find((type) => type.id === selectedFilter.value)
          ?.label,
      });
    case "transaction-document-type":
      return message({
        id: transactionDocumnetTypes.find(
          (type) => type.id === selectedFilter.value,
        )?.label,
      });
    case "multiinput":
      return selectedFilter.value.join(", ");
    case "redemption-code-status":
      return message({
        id: redemptionCodeStatuses.find(
          (type) => type.id === selectedFilter.value,
        )?.label,
      });
    case "segment-single":
      return selectedFilter.value?.label;
    default:
      const isValidJSON = isJSON(selectedFilter.value);
      if (isValidJSON && typeof selectedFilter.value === "string")
        return JSON.parse(selectedFilter.value);

      return selectedFilter.value;
  }
};

export const getValidationSchema = (message: any) => {
  return object().shape({
    filters: array().of(
      object().shape({
        label: string().required(),
        condition: string().when(["type", "query"], ([type, query], schema) => {
          const textConditionQueries = [
            "address:address1",
            "address:address2",
            "address:city",
            "referralToken",
            "firstName",
            "lastName",
            "loyaltyCardNumber",
            "address:postal",
            "address:province",
            "address:street",
            "refereeName",
            "referrerName",
            "referrerToken",
            "name",
            "identifier",
            "customerData:email",
            "customerData:loyaltyCardNumber",
            "customerData:phone",
            "type",
            "customerData:firstName",
            "customerData:lastName",
            "token",
            "code",
            "unitPluralName",
            "entityId",
            "entityType",
            "eventType",
            "ip",
            "username",
            "userType",
            "comment",
            "customerData:name",
            "header:purchasePlace",
            "url",
            "description",
            "currency",
            "localeCode",
          ];
          let shouldRequireCondition = false;
          switch (type) {
            case "text":
              shouldRequireCondition = textConditionQueries.includes(query);
              break;
            case "datetime":
            case "date":
            case "number":
            case "timeline-event-type":
            case "reward-fulfillment-status":
            case "member-email-filter":
            case "member-phone-filter":
            case "wallet-type-code-filter":
            case "achievement-name":
            case "event-system-identifier-filter":
            case "member-loyalty-card-number":
            case "transaction-document-number":
              shouldRequireCondition = true;
              break;
            default:
              shouldRequireCondition = false;
          }

          return shouldRequireCondition
            ? schema.required(message({ id: "requiredFieldError" }))
            : schema.notRequired();
        }),
        value: mixed()
          .required(message({ id: "requiredFieldError" }))
          .when("type", {
            is: (type: string) => type === "multiwithmodal",
            then: (schema) =>
              schema.test(
                "min-items",
                message({ id: "requiredArray" }),
                (value) => {
                  if (!value) return false;
                  return Array.isArray(value) && value.length > 0;
                },
              ),
            otherwise: (schema) => schema,
          }),
      }),
    ),
  });
};

export const getNewFiltersOnly = (
  filters: SelectedFilter[],
): SelectedFilter[] => {
  return filters.filter((filter) => "label" in filter);
};

export const parseSingleFilter = (
  filter: SelectedFilter & { condition?: string },
): BackendFilter | undefined => {
  if (!filter || !filter.value) return;
  if (Array.isArray(filter.value) && filter.value.length === 0) return;

  switch (filter.type) {
    case "boolean":
    case "boolean-status":
    case "boolean-yes-no":
    case "text":
    case "direct-trigger-filter":
    case "referral-trigger-filter":
    case "gender":
    case "tier-name-with-id-filter":
    case "bulk-actions":
    case "event-name-filter":
    case "event-system-identifier-filter":
    case "number":
    case "member-email-filter":
    case "member-phone-filter":
    case "timeline-event-type":
    case "wallet-type-code-filter":
    case "achievement-name":
    case "reward-name-filter":
    case "reward-types-filter_eq":
    case "reward-fulfillment-status":
    case "member-loyalty-card-number":
    case "wallet-type-name-filter":
    case "import-type":
    case "export-type":
    case "export-status":
    case "points-transfers-types":
    case "transaction-document-number":
    case "transaction-document-type":
    case "multiinput":
    case "webhook-name-filter":
    case "tier-name-filter":
    case "redemption-code-status":
    case "import-details-status":
    case "segment-single":
      return {
        value: filter.value,
        query: filter.query,
        condition: filter.condition || "",
      };
    case "datetime":
      return {
        value: OLDate(filter.value, {
          formatString: "yyyy-MM-dd hh:mm a",
        }),
        query: filter.query,
        condition: filter.condition || "",
      };
    case "multiwithmodal": {
      return {
        value: filter.value
          .map((item: KeyValuePair) => `(${item.key};${item.value})`)
          .join(),
        query: filter.query,
        condition: "",
      };
    }
    case "direct-campaign-name-filter":
    case "time-campaign-name-filter":
    case "referral-name-filter":
    case "campaign-name-filter":
    case "tenant-name":
      return {
        value: filter.value.label,
        query: filter.query,
        condition: "eq",
      };
    case "country-filter":
    case "channel-filter":
      return {
        value: filter.value.id,
        query: filter.query,
        condition: "",
      };
    case "reward-categories":
    case "tiers":
      return {
        value: filter.value.map((filter: Item) => filter.id),
        query: filter.query,
        condition: "in",
      };
    case "reward-types-filter":
      return {
        value: filter.value,
        query: filter.query,
        condition: "isType",
      };
    case "segments":
      return {
        value: filter.value,
        query: filter.query,
        condition: "in",
      };
  }
};

export const parseFiltersForBE = (filters: SelectedFilter[]): BackendFilter[] =>
  filters
    .filter((filter) => filter?.value && filter.type)
    .map((filter) => parseSingleFilter(filter))
    .filter((f): f is BackendFilter => f !== undefined);

// Some filters need to have initial value to work properly
export const pushFilterWithInitialValue = (filter: SelectedFilter) => {
  switch (filter.type) {
    case "multiwithmodal":
    case "multiinput":
      return { ...filter, value: [] };
    default:
      return filter;
  }
};

export const conditionItems = {
  BEFORE: {
    id: "lt",
    label: "before",
  },
  BEFORE_EQUAL: {
    id: "lte",
    label: "beforeOrEqual",
  },
  AFTER: { id: "gt", label: "after" },
  AFTER_EQUAL: { id: "gte", label: "afterOrEqual" },
  LESS_THAN: {
    id: "lt",
    label: "lt",
  },
  LESS_EQUAL: {
    id: "lte",
    label: "lte",
  },
  GREATER_THAN: {
    id: "gt",
    label: "gt",
  },
  GREATER_EQUAL: {
    id: "gte",
    label: "gte",
  },
  EQ: { id: "eq", label: "eq" },
  LIKE: { id: "like", label: "like" },
  IN: { id: "in", label: "in" },
  NOT_IN: { id: "notIn", label: "notIn" },
};

export const getConditions = (conditionArray: PossibleConditions[]) =>
  conditionArray.map((condition) => conditionItems[condition]);
